<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { fly } from "svelte/transition";
  import moment from "moment";

  import styles from "./styles";
  import T from "./texts";
  import { ChatHistoryStore } from "../../lib/stores/widget";
  import { lightenColor, darkenColor } from "../../lib/color";
  import { getThumbnailURL, getLoadingGifURL } from "../../lib";
  import type { TicketEvent } from "../../types/ticket";
  import LogoIcon from "./LogoIcon.svelte";
  import ChatHeader from "./Chat/Header.svelte";
  import NewAgentAnnouncement from "./Chat/NewAgentAnnouncement.svelte";
  import AgentMessage from "./Chat/AgentMessage.svelte";
  import WidgetMessage from "./Chat/WidgetMessage.svelte";
  import WidgetImageMessage from "./Chat/WidgetImageMessage.svelte";
  import BotMessage from "./Chat/BotMessage.svelte";
  import EmailRequestMessage from "./Chat/EmailRequestMessage.svelte";
  import ReplySection from "./Chat/ReplySection.svelte";
  import FAQList from "./Chat/FAQList.svelte";
  import TypingIndicator from "./Icons/TypingIndicator.svelte";

  export let chatHidden: boolean;
  export let settings: any; // TODO: Define proper type
  export let language: string;
  export let fontColor;
  export let mainColor;
  export let isOnline;
  export let nextOnlineTime;
  export let position = "right";
  export let gradientColor;
  export let loading = false;
  export let agentTyping = false;

  let history: TicketEvent[] = [];
  let lastHistoryItem: TicketEvent | null = null;
  let email: string;
  let emailSubmitted = false;
  let currency = "USD";
  let tvScroll: HTMLElement;

  const s = styles({
    headerColor: settings?.website_details?.button_background_color,
    fontColor,
    headerLighterColor: lightenColor(mainColor),
    headerDarkerColor: darkenColor(mainColor),
    gradientColor,
  });
  const dispatch = createEventDispatcher();
  const marginTops = {
    agent_message: "18px",
    widget_message: "10px",
    bot_message: "5px",
    new_agent_announcement: "0px",
    ask_email: "0px",
  };

  function sendMessage(message: string) {
    if (!message || message.length === 0) return;

    dispatch("message", message);
    scrollToBottom();
  }

  function sendAttachment(attachment: File) {
    if (!attachment) return;

    let imageBinary;
    const reader = new FileReader();
    reader.onload = function (e) {
      imageBinary = e.target?.result;

      dispatch("attachment", imageBinary);
      scrollToBottom();
    };

    reader.onerror = function (e) {
      console.error("Error reading attachment", e);
    };

    reader.readAsDataURL(attachment);
  }

  function sendFaq(faq: { question: string; answer: string }) {
    if (!faq.question || !faq.answer) return;

    dispatch("faq", faq);
    scrollToBottom();
  }

  function scrollToBottom() {
    setTimeout(() => {
      const scroll = tvScroll;
      if (scroll) {
        scroll.scrollTop = scroll.scrollHeight;
      }
    }, 100);
  }

  function submitEmail(event: Event) {
    dispatch("email", email);
    emailSubmitted = true;
    event.preventDefault();
    scrollToBottom();
    return false;
  }

  function showTypingIndicator(showIndicator: boolean) {
    dispatch("typing", showIndicator);
  }

  function preloadImage() {
    if (settings.user_ids) {
      settings.user_ids.forEach((userId: string) => {
        const img = new Image();
        img.src = getThumbnailURL(userId);
      });
    }
  }

  function showInput(
    lastType: string | undefined,
    hasCustomerContact: boolean,
    emailSubmitted: boolean,
  ) {
    return lastType !== "ask_email" || hasCustomerContact || emailSubmitted;
  }

  function computeMarginTop(historyIndex: number, type: string) {
    const isCustomerMessage = type === "widget_message";
    const previousItem = history[historyIndex - 1];
    const currentItem = history[historyIndex];

    if (!previousItem) {
      return isCustomerMessage ? "10px" : "0px";
    }

    if (previousItem.type !== type) {
      return "10px";
    }

    const previousItemDate: any = new Date(previousItem.created_at);
    const currentItemDate: any = new Date(currentItem.created_at);
    const timeDiff = Math.abs(previousItemDate - currentItemDate);
    const minuteMs = 1000 * 60;
    if (timeDiff < minuteMs) return "8px";

    return marginTops[currentItem.type as keyof typeof marginTops] || "10px";
  }

  $: settings && preloadImage();

  $: currency = settings?.currency || "USD";

  $: !chatHidden && scrollToBottom();

  $: showBranding = settings.plan_code === "2024basic" || !settings.plan_code;

  $: if (loading || agentTyping) {
    scrollToBottom();
  }

  ChatHistoryStore.subscribe((h) => {
    history = h;

    if (history) {
      lastHistoryItem = history[history.length - 1];
    }

    scrollToBottom();
  });

  //HACK: momentjs time refresh every minute
  let timer: ReturnType<typeof setInterval> | null = null;
  onMount(() => {
    timer = setInterval(() => {
      if (!tvScroll) return;

      const elements = tvScroll.querySelectorAll("[data-datetime]");
      for (var el of elements) {
        if (el instanceof HTMLElement) {
          el.innerHTML = moment(el.dataset.datetime).fromNow();
        }
      }
    }, 60 * 1000);
  });
  onDestroy(() => {
    if (timer) {
      clearInterval(timer);
    }
  });
</script>

{#if !chatHidden}
  <div
    class={gradientColor ? s.chatContainerGradient : s.chatContainer}
    style={`${position}: 0;`}
    in:fly={{ duration: 400, y: 20 }}
    out:fly={{ duration: 200, y: 20 }}
  >
    <ChatHeader
      {s}
      {isOnline}
      {nextOnlineTime}
      {settings}
      {language}
      {gradientColor}
      bind:chatHidden
    />
    <div
      class={s.chatPane}
      id="tv-scroll"
      bind:this={tvScroll}
      style={showInput(
        lastHistoryItem?.type,
        settings.has_customer_contact,
        emailSubmitted,
      )
        ? ""
        : "border-radius: 0 0 20px 20px;"}
    >
      {#each history as c, i}
        <div in:fly={{ duration: 600, y: 10, delay: 200 }}>
          {#if c.type === "new_agent_announcement"}
            <NewAgentAnnouncement {s} ticketEvent={c} {language} />
          {:else if c.type === "agent_message"}
            <AgentMessage
              {s}
              ticketEvent={c}
              {language}
              marginTop={computeMarginTop(i, "agent_message")}
              {currency}
              isLastMessage={i === history.length - 1}
              accountId={settings.account_id}
            />
          {:else if c.type === "widget_message"}
            <WidgetMessage
              {s}
              ticketEvent={c}
              marginTop={computeMarginTop(i, "widget_message")}
            />
          {:else if c.type === "bot_message"}
            <BotMessage
              {s}
              ticketEvent={c}
              marginTop={computeMarginTop(i, "bot_message")}
              agentName={settings.name}
              {language}
            />
          {:else if c.type == "ask_email"}
            <EmailRequestMessage
              {s}
              agentName={settings.name}
              {language}
              {emailSubmitted}
              {isOnline}
              {submitEmail}
              bind:email
            />
          {:else if c.type == "widget_image_message" && c.payload.attachment_url}
            <WidgetImageMessage
              {s}
              marginTop={computeMarginTop(i, "widget_message")}
              url={c.payload.attachment_url}
            />
          {/if}
        </div>
      {/each}

      {#if loading}
        <div class={s.spacer}>
          <div
            class={s.chatBubbleAgent}
            style="margin-left: 0 !important;"
            out:fly={{ duration: 220, y: -30 }}
          >
            <img style="width: 40px;" src={getLoadingGifURL()} alt="..." />
          </div>
        </div>
      {/if}

      {#if agentTyping}
        <div class={s.typingSpacer}>
          <TypingIndicator />
        </div>
      {/if}

      {#if history.length === 0 && !showBranding}
        <div class={s.noMessages}>
          {T(language, "Welcome to our store! We're here to help.", [])}
        </div>

        {#if settings.faq_enabled}
          <FAQList {s} faqItems={settings.faq_items} {sendFaq} />
        {/if}
      {:else}
        <div class={s.bottomSpacer}>&nbsp;</div>
      {/if}

      {#if settings.faq_enabled && history.length === 1}
        <FAQList {s} faqItems={settings.faq_items} {sendFaq} />
      {/if}
    </div>
    {#if showInput(lastHistoryItem?.type, settings.has_customer_contact, emailSubmitted)}
      <div class={s.divider} />
      <div class={s.chatFooter}>
        <ReplySection
          {s}
          {sendMessage}
          {position}
          {sendAttachment}
          {showTypingIndicator}
        />
        {#if showBranding}
          <div class={s.poweredBy}>
            Powered by <LogoIcon />Talkvisor
          </div>
        {/if}
      </div>
    {/if}
  </div>
{/if}
